import { useMemo } from 'react';
import { bool, func, object, shape } from 'prop-types';
import { cloneDeep, defaults, forEach, isEqual, isFunction, merge, omit, pick } from 'lodash';
import { createContainer, createHook, createStore } from 'react-sweet-state';

import FilterRegistry from '../components/page-framework/filter/FilterRegistry';
import { setUrlFromEncodedState } from '../utilities/FilterStateUrlUtilities';
import { DataServiceFactory } from '../services/ApiDataService';
import { filterId as timeRangeFilterId } from '../components/filters/time-range-filter/TimeRangeFilter';

// Integrate react-sweet-state with the redux dev tools.
defaults.devtools = true;

const DEFAULT_NAME = 'global';

export const filterStateType = shape({
    initialized: bool,
    options: object,
    previous: object,
    current: object,
    encoded: object,
    summarized: object,
    parameters: object,
});

export const filterStateActionsType = shape({
    initialize: func,
    setFilterState: func,
    clearFilterState: func,
    reset: func,
});

export const INITIAL_STATE = {
    name: DEFAULT_NAME,
    initialized: false,
    options: {},
    previous: {},
    current: {},
    encoded: {},
    summarized: {},
    parameters: {},
};

/**
 * This merges one or more filter states into a single state, giving precedence from left to right.
 */
export function mergeFilterStates(...filterStates) {
    const statesToMerge = [...filterStates];

    return statesToMerge.reduce(
        (mergedState, stateToMerge) => {
            const options = defaults(mergedState.options, stateToMerge.options);
            const previous = defaults(mergedState.previous, stateToMerge.previous);
            const current = defaults(mergedState.current, stateToMerge.current);
            const encoded = defaults(mergedState.encoded, stateToMerge.encoded);
            const summarized = defaults(mergedState.summarized, stateToMerge.summarized);
            const parameters = defaults(mergedState.parameters, stateToMerge.parameters);

            return {
                options,
                previous,
                current,
                encoded,
                summarized,
                parameters,
                initialized: mergedState.initialized && stateToMerge.initialized,
            };
        },
        {
            ...cloneDeep(INITIAL_STATE),
            initialized: true,
        }
    );
}

/**
 * This picks the states for the specified filters into a separate state containing only the
 * specified filter states.
 */
export function pickFilterStates(state, filterIds) {
    const options = pick(state.options, filterIds);
    const previous = pick(state.previous, filterIds);
    const current = pick(state.current, filterIds);
    const encoded = pick(state.encoded, filterIds);
    const summarized = pick(state.summarized, filterIds);
    const parameters = pick(state.parameters, filterIds);

    return {
        initialized: state.initialized,
        options,
        previous,
        current,
        encoded,
        summarized,
        parameters,
    };
}

/**
 * This checks whether the specified state has state for all of the specified filters.
 */
export function hasFilterStates(state, filterIds) {
    const current = pick(state.current, filterIds);

    return Object.keys(current).length === filterIds.length;
}

export function transformParameters(state, filterTransformations = {}) {
    const transformedStates = cloneDeep(state);

    forEach(filterTransformations, (transformFunction, filterId) => {
        const stateToTransform = transformedStates.current[filterId];
        if (stateToTransform) {
            const filterStateService = FilterRegistry.getFilterStateService(
                filterId,
                transformedStates.options?.[filterId]
            );
            const transformedState = transformFunction(stateToTransform);

            transformedStates.parameters[filterId] = filterStateService.parameterize(transformedState);
        }
    });

    return transformedStates;
}

/**
 * Initializes the filter state for the specified filters using the supplied encoded filter state.
 *
 * @param {Object} encodedFilterState The encoded filter state from the URL to use when initializing the filter state.
 * @param {Array} activeFilterIds An optional array containing the IDs of filters that should have active state.  If
 * present, only these filters will be initialized, regardless of the state defined in `encodedFilterState`.
 * @param {Object} filterOptions The options for each filter.
 */
function initialize(encodedFilterState, activeFilterIds, filterOptions = {}) {
    return ({ getState, setState }, { name, dataService = DataServiceFactory(), manageUrl = true }) => {
        let canceled = false;
        const state = getState();

        // If a previous initialization is in process, cancel it.  We want to initialize with the
        // latest state.
        if (isFunction(state.cancelInitialization)) state.cancelInitialization();

        setState({
            ...cloneDeep(INITIAL_STATE),
            name,
            cancelInitialization: () => (canceled = true),
        });

        const filterIds = activeFilterIds || Object.keys(encodedFilterState || {});

        Promise.all(
            filterIds.map((filterId) => {
                const filterStateService = FilterRegistry.getFilterStateService(filterId, filterOptions?.[filterId]);
                const possiblePromise = filterStateService.createState(encodedFilterState, dataService);

                return Promise.resolve(possiblePromise).then((filterState) => {
                    if (filterState) {
                        return {
                            current: {
                                [filterId]: filterState,
                            },
                            encoded: {
                                [filterId]: filterStateService.encode(filterState),
                            },
                            summarized: {
                                [filterId]: filterStateService.summarize(filterState),
                            },
                            parameters: {
                                [filterId]: filterStateService.parameterize(filterState),
                            },
                        };
                    }

                    return {};
                });
            })
        ).then((results) => {
            // If the initialization has been canceled, just ignore the results.
            if (canceled) return;

            setState(
                merge(
                    {
                        ...cloneDeep(INITIAL_STATE),
                        name,
                        filterOptions,
                        initialized: true,
                        cancelInitialization: undefined,
                    },
                    ...results
                )
            );

            if (manageUrl) setUrlFromEncodedState(getState()?.encoded);
        });
    };
}

/**
 * Resets to the initial state.
 */
function reset() {
    return ({ setState }, { name }) => {
        setState({
            ...cloneDeep(INITIAL_STATE),
            name,
        });
    };
}

const FilterStateStore = createStore({
    name: 'FilterState',

    initialState: cloneDeep(INITIAL_STATE),

    actions: {
        initialize,
        reset,

        /**
         * Sets the state for the specified filter.
         *
         * @param {String} filterId The ID of the filter to set state for.
         * @param {Object} filterState The state to set for the filter.
         */
        setFilterState: (filterId, filterState) => {
            return ({ setState, getState }, { manageUrl = true }) => {
                const state = getState();
                const filterStateService = FilterRegistry.getFilterStateService(filterId, state?.options?.[filterId]);
                const previous = cloneDeep(state.current);
                const current = {
                    ...state.current,
                    [filterId]: cloneDeep(filterState),
                };

                // Only update the state if the changed filter is the time range filter or if the
                // state actually changed.  This prevents unnecessary state updates but allows the
                // time range filter's refresh button to update the state and trigger a data refresh.
                if (filterId === timeRangeFilterId || !isEqual(previous, current)) {
                    const encoded = {
                        ...state.encoded,
                        [filterId]: filterStateService.encode(filterState),
                    };
                    const summarized = {
                        ...state.summarized,
                        [filterId]: filterStateService.summarize(filterState),
                    };
                    const parameters = {
                        ...state.parameters,
                        [filterId]: filterStateService.parameterize(filterState),
                    };

                    setState({
                        previous,
                        current,
                        encoded,
                        summarized,
                        parameters,
                    });

                    if (manageUrl) setUrlFromEncodedState(getState()?.encoded);
                }
            };
        },

        /**
         * Clears the filter state for the specified filter.
         *
         * @param {String} filterId The ID of the filter to clear the state for.
         */
        clearFilterState: (filterId) => {
            return ({ setState, getState }, { manageUrl = true }) => {
                const state = getState();
                const previous = cloneDeep(state.current);
                const current = omit(state.current, filterId);
                const encoded = omit(state.encoded, filterId);
                const summarized = omit(state.summarized, filterId);
                const parameters = omit(state.parameters, filterId);

                setState({
                    previous,
                    current,
                    encoded,
                    summarized,
                    parameters,
                });

                if (manageUrl) setUrlFromEncodedState(getState()?.encoded);
            };
        },
    },
});

export const FilterStateContainer = createContainer(FilterStateStore, {
    onInit:
        () =>
        ({ dispatch, setState }, { name, state, encodedState }) => {
            if (state) {
                setState({
                    ...state,
                    name: name || DEFAULT_NAME,
                });
            } else if (encodedState) {
                dispatch(initialize(encodedState));
            }
        },
    onUpdate:
        () =>
        ({ dispatch, setState }, { name, state, encodedState }) => {
            if (state) {
                setState({
                    ...state,
                    name: name || DEFAULT_NAME,
                });
            } else if (encodedState) {
                dispatch(initialize(encodedState));
            }
        },
});

const _useFilterState = createHook(FilterStateStore);

/**
 * This overrides the default hook so that it returns undefined until the
 * filter state is fully initialized.
 */
export function useFilterState() {
    const [rawFilterState, filterStateActions] = _useFilterState();

    // Ensure the returned filter state is undefined until all filters have
    // initialized.
    const filterState = useMemo(() => {
        if (rawFilterState.initialized) return rawFilterState;
        return undefined;
    }, [rawFilterState]);

    return [filterState, filterStateActions];
}

export const useCurrentFilterState = createHook(FilterStateStore, {
    selector: (state) => state?.current,
});

export const useCurrentFilterStateById = createHook(FilterStateStore, {
    selector: (state, filterId) => state?.current?.[filterId],
});

export const useEncodedFilterState = createHook(FilterStateStore, {
    selector: (state) => state?.encoded,
});

export const useEncodedFilterStateById = createHook(FilterStateStore, {
    selector: (state, filterId) => state?.encoded?.[filterId],
});

export const useSummarizedFilterState = createHook(FilterStateStore, {
    selector: (state) => state?.summarized,
});

export const useSummarizedFilterStateById = createHook(FilterStateStore, {
    selector: (state, filterId) => state?.summarized?.[filterId],
});

export const useParameterizedFilterState = createHook(FilterStateStore, {
    selector: (state) => state?.parameters,
});

export const useParameterizedFilterStateById = createHook(FilterStateStore, {
    selector: (state, filterId) => state?.parameters?.[filterId],
});

export const useIsFilterActive = createHook(FilterStateStore, {
    selector: (state, filterId) => !!state?.current?.[filterId],
});
